/**
 * Person Slide-over Alpine.js Component
 *
 * Manages the person/tenant detail slide-over panel state and data fetching.
 * Supports inline editing for contact details.
 * Can be opened from tenancy slideover (nested) or People index.
 */

import consumer from "../channels/consumer"
import { notesMixin, destroyNote } from './notes_mixin'
import { gravatarUrl } from '../utils/md5'
import { complianceMixin } from './compliance_mixin'
import { withMixins } from '../utils/with_mixins'

export function personSlideover() {
  return withMixins(complianceMixin(), {
    ...notesMixin,
    // State
    open: false,
    loading: false,
    error: null,
    person: null,
    personId: null,
    subscription: null,
    zIndex: 100010,

    // Context state (determines which accordions to show)
    // Values: 'enquiry', 'viewing', 'tenancy', 'general'
    context: 'general',

    // Enquiry focus state (when opened from enquiries index)
    focusEnquiryId: null,
    expandedEnquiryId: null,

    // Note form state (person-level notes in History section)
    noteText: '',
    noteSaving: false,
    noteError: null,

    // Not interested action state
    notInterestedSaving: false,

    // Tab state
    activeTab: 'overview',

    // Accordion state (multiple sections can be open) — used within tabs (e.g. compliance check types)
    openSections: {},

    // Viewing tab state — expanded viewing details and action states
    expandedViewingId: null,
    expandedViewing: null,       // Full viewing data fetched from /viewings/:id.json
    viewingLoading: false,
    // Viewing action states (apply to currently expanded viewing)
    vwShowCancelForm: false,
    vwShowRescheduleForm: false,
    vwShowRejectForm: false,
    vwCancelReason: '',
    vwRejectReason: '',
    vwCancelling: false,
    vwRejecting: false,
    vwRescheduling: false,
    vwConfirming: false,
    vwCompleting: false,
    vwMarkingNoShow: false,
    vwSendingPostViewingEmail: false,
    vwSavingNotes: false,
    vwNotes: '',
    // Reschedule form state
    vwRescheduleSlots: [],
    vwLoadingRescheduleSlots: false,
    vwRescheduleDate: '',
    vwRescheduleTime: '',
    vwRescheduleUseCustomTime: false,
    vwRescheduleReason: '',

    // Bank details modal state
    bankModalOpen: false,
    bankSaving: false,
    bankError: null,
    bankPasswordRequired: false,
    bankForm: {},

    // Contact modal state
    contactModalOpen: false,
    contactSaving: false,
    contactError: null,
    contactForm: {
      organisation_name: '',
      first_name: '',
      last_name: '',
      salutation: '',
      email: '',
      phone: '',
      phone_2: '',
      address_line_1: '',
      address_line_2: '',
      town: '',
      postcode: ''
    },

    // Legacy inline editing state (kept for compatibility)
    editing: {
      email: false,
      phone: false,
      phone_2: false
    },
    editValues: {
      email: '',
      phone: '',
      phone_2: ''
    },
    saving: {
      email: false,
      phone: false,
      phone_2: false
    },
    saveError: null,

    // Compliance state and methods are provided by complianceMixin()
    // (docUpload, lightbox, overrideModal, referencingModal, and all compliance methods)

    // Initialize component
    init() {
      // CRITICAL: Capture Alpine context for ActionCable callbacks
      const self = this

      // Subscribe to ActionCable for real-time updates
      this.subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            // Filter for person updates matching our current person
            if (message.type === 'person' && self.person && message.id === self.person.id) {
              console.debug('[PersonSlideover] Received update for person', message.id)
              self.handlePersonUpdate(message)
            }
            // Handle viewing updates - refresh person data if viewing is for this person
            if (message.type === 'viewing' && self.person && self.open) {
              const messagePersonId = message.data?.person?.id
              if (messagePersonId && String(messagePersonId) === String(self.person.id)) {
                console.debug('[PersonSlideover] Received viewing update for person', self.person.id)
                self.fetchPerson()
              }
            }
            // Handle enquiry updates - refresh person data if enquiry is for this person
            if (message.type === 'enquiry' && self.person && self.open) {
              const messagePersonId = message.data?.person?.id
              if (messagePersonId && String(messagePersonId) === String(self.person.id)) {
                console.debug('[PersonSlideover] Received enquiry update for person', self.person.id)
                self.fetchPerson()
              }
            }
          }
        }
      )

      // Listen for open requests
      this.$watch('open', (value) => {
        if (value && this.personId && !this.person) {
          this.fetchPerson()
        }
      })
    },

    // Open slide-over for a specific person
    // context: 'enquiry', 'viewing', 'tenancy', or 'general'
    // focusEnquiry: optional enquiry ID to highlight (when coming from enquiries index)
    openForPerson(personId, focusEnquiry = null, context = 'general') {
      const store = Alpine.store?.('slideovers')

      // If this person is already open AND visible, just bring it to front
      if (store?.isOpen?.('person', personId) && this.open && this.personId === personId) {
        store.bringToFront('person', personId)
        return
      }

      // Clean up stale store entry (e.g. after Turbo navigation)
      if (store?.unregister && this.personId) {
        store.unregister('person', this.personId)
      }

      // Get next z-index from store for proper stacking
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }
      this.personId = personId
      this.focusEnquiryId = focusEnquiry
      this.context = context
      this.expandedEnquiryId = null
      this.expandedViewingId = null
      this.expandedViewing = null
      this.person = null
      this.error = null
      this.activeTab = this.getDefaultTab(context, focusEnquiry)
      this.openSections = {}
      this.resetEditingState()
      this.resetNoteState()
      this.resetViewingActionState()
      this.open = true

      // Register this slideover
      if (store?.register) {
        store.register('person', personId, this)
      }

      this.fetchPerson()
    },

    // Legacy context helpers (kept for compatibility, now delegated to tab visibility)
    showEnquiryContext() { return this.isTabVisible('enquiries') },
    showComplianceContext() { return this.isTabVisible('compliance') },

    // Close slide-over
    close() {
      // Unregister from store
      const store = Alpine.store?.('slideovers')
      if (store?.unregister && this.personId) {
        store.unregister('person', this.personId)
      }

      // Reset state before closing
      this.activeTab = 'overview'
      this.openSections = {}
      this.expandedEnquiryId = null
      this.expandedViewingId = null
      this.expandedViewing = null
      this.focusEnquiryId = null
      this.resetEditingState()
      this.resetNoteState()
      this.resetViewingActionState()
      this.resetAllDocUploads()

      // Use $nextTick to ensure accordion collapse starts before panel closes
      this.$nextTick(() => {
        this.open = false
      })
    },

    // Reset all editing state
    resetEditingState() {
      this.editing = { email: false, phone: false, phone_2: false }
      this.editValues = { email: '', phone: '', phone_2: '' }
      this.saving = { email: false, phone: false, phone_2: false }
      this.saveError = null
    },

    // Reset note form state
    resetNoteState() {
      this.noteText = ''
      this.noteSaving = false
      this.noteError = null
    },

    // Fetch person data from server
    async fetchPerson() {
      if (!this.personId) return

      this.loading = true
      this.error = null

      try {
        const response = await fetch(`/people/${this.personId}.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        this.person = await response.json()

        // Validate that the selected tab is actually visible with this person's data
        this.validateActiveTab()

        // Auto-expand focused enquiry if opened from enquiries context
        if (this.focusEnquiryId && this.activeTab === 'enquiries') {
          this.expandedEnquiryId = this.focusEnquiryId
        }

        // Auto-switch to compliance tab if checks are awaiting approval
        const checksAwaitingApproval = this.person.tenant_checks?.filter(c => c.status === 'awaiting_approval') || []
        if (checksAwaitingApproval.length > 0 && this.activeTab === 'overview') {
          this.activeTab = 'compliance'
          checksAwaitingApproval.forEach(check => {
            this.getDocUploadState(check.check_type).open = true
          })
        }
      } catch (error) {
        console.error('Failed to fetch person:', error)
        this.error = 'Failed to load person details. Please try again.'
      } finally {
        this.loading = false
      }
    },

    // Start editing a field
    startEditing(field) {
      this.editValues[field] = this.person[field] || ''
      this.editing[field] = true
      this.saveError = null

      // Focus the input after render
      this.$nextTick(() => {
        const input = this.$el.querySelector(`[data-edit-field="${field}"]`)
        if (input) {
          input.focus()
          input.select()
        }
      })
    },

    // Cancel editing a field
    cancelEditing(field) {
      this.editing[field] = false
      this.editValues[field] = ''
      this.saveError = null
    },

    // Save a field
    async saveField(field) {
      if (this.saving[field]) return

      const newValue = this.editValues[field]?.trim() || null

      // Skip if unchanged
      if (newValue === (this.person[field] || null)) {
        this.editing[field] = false
        return
      }

      this.saving[field] = true
      this.saveError = null

      try {
        const response = await fetch(`/people/${this.personId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            person: { [field]: newValue }
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        const updatedPerson = await response.json()
        this.person = updatedPerson
        this.editing[field] = false
      } catch (error) {
        console.error(`Failed to save ${field}:`, error)
        this.saveError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.saving[field] = false
      }
    },

    // Handle keyboard events for inline editing
    handleEditKeydown(event, field) {
      if (event.key === 'Enter') {
        event.preventDefault()
        this.saveField(field)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        this.cancelEditing(field)
      }
    },

    getInitials(name) {
      if (!name) return '?'
      name = name.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
      return name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
    },

    // Open bank details modal
    openBankModal() {
      this.bankForm = {
        account_name: this.person?.account_name || '',
        sort_code: this.person?.sort_code || '',
        account_number: this.person?.account_number || '',
        current_password: ''
      }
      this.bankError = null
      this.bankPasswordRequired = false
      this.bankModalOpen = true
    },

    closeBankModal() {
      this.bankModalOpen = false
      this.bankError = null
      this.bankPasswordRequired = false
    },

    async saveBankDetails() {
      this.bankSaving = true
      this.bankError = null

      try {
        const { current_password, ...bankFields } = this.bankForm
        const response = await fetch(`/people/${this.personId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ current_password, person: bankFields })
        })

        if (!response.ok) {
          const errorData = await response.json()
          if (errorData.password_required) {
            this.bankPasswordRequired = true
          }
          throw new Error(errorData.error || errorData.errors?.[0] || 'Failed to save')
        }

        this.person = await response.json()
        this.bankForm.current_password = ''
        this.bankPasswordRequired = false
        this.closeBankModal()
      } catch (error) {
        console.error('Failed to save bank details:', error)
        this.bankError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.bankSaving = false
      }
    },

    // Open contact details modal
    openContactModal() {
      this.contactForm = {
        organisation_name: this.person?.organisation_name || '',
        first_name: this.person?.first_name || '',
        last_name: this.person?.last_name || '',
        salutation: this.person?.salutation || '',
        email: this.person?.email || '',
        phone: this.person?.phone || '',
        phone_2: this.person?.phone_2 || '',
        address_line_1: this.person?.address_line_1 || '',
        address_line_2: this.person?.address_line_2 || '',
        town: this.person?.town || '',
        postcode: this.person?.postcode || ''
      }
      this.contactError = null
      this.contactModalOpen = true
    },

    // Close contact details modal
    closeContactModal() {
      this.contactModalOpen = false
      this.contactError = null
    },

    // Save contact details from modal
    async saveContactDetails() {
      this.contactSaving = true
      this.contactError = null

      try {
        const response = await fetch(`/people/${this.personId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            person: {
              organisation_name: this.person?.is_organisation ? (this.contactForm.organisation_name?.trim() || null) : undefined,
              first_name: this.contactForm.first_name?.trim() || null,
              last_name: this.contactForm.last_name?.trim() || null,
              salutation: this.contactForm.salutation?.trim() || null,
              email: this.contactForm.email?.trim() || null,
              phone: this.contactForm.phone?.trim() || null,
              phone_2: this.contactForm.phone_2?.trim() || null,
              address_line_1: this.contactForm.address_line_1?.trim() || null,
              address_line_2: this.contactForm.address_line_2?.trim() || null,
              town: this.contactForm.town?.trim() || null,
              postcode: this.contactForm.postcode?.trim()?.toUpperCase() || null
            }
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save contact details')
        }

        const updatedPerson = await response.json()
        this.person = updatedPerson
        this.closeContactModal()
      } catch (error) {
        console.error('Failed to save contact details:', error)
        this.contactError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.contactSaving = false
      }
    },

    // Handle real-time updates from ActionCable
    handlePersonUpdate(message) {
      if (!this.person || message.id !== this.person.id) return

      switch (message.action) {
        case 'update':
          // Merge updated data
          Object.assign(this.person, message.data)
          break
        case 'touchpoint_sent':
        case 'activity_added':
          // Add new activity to the timeline
          if (this.person.activities && message.activity) {
            this.person.activities.unshift(message.activity)
          }
          // Update enquiry status if provided
          if (this.person.enquiries && message.enquiry) {
            const enquiry = this.person.enquiries.find(e => e.id === message.enquiry.id)
            if (enquiry) {
              Object.assign(enquiry, message.enquiry)
            }
          }
          break
        case 'destroy':
          this.close()
          break
      }
    },

    // Computed helpers for template
    get typeBadgeColor() {
      if (!this.person) return 'gray'
      return this.person.is_organisation ? 'purple' : 'blue'
    },

    get roleBadges() {
      if (!this.person) return []
      // Show only the most important role
      // (hierarchy: Current Tenant > Upcoming Tenant > Past Tenant > Applicant > Enquirer)
      if (this.person.is_current_tenant) {
        return [{ label: 'Current Tenant', color: 'green' }]
      }
      if (this.person.is_upcoming_tenant) {
        return [{ label: 'Upcoming Tenant', color: 'blue' }]
      }
      if (this.person.is_tenant) {
        return [{ label: 'Past Tenant', color: 'gray' }]
      }
      if (this.person.is_applicant) {
        return [{ label: 'Applicant', color: 'yellow' }]
      }
      if (this.person.is_enquirer) {
        return [{ label: 'Enquirer', color: 'blue' }]
      }
      return []
    },

    get hasBankDetails() {
      return this.person?.has_bank_details
    },

    get hasAddress() {
      return this.person?.has_address
    },

    // Format currency
    formatCurrency(amount) {
      if (amount == null) return '—'
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    },

    // Format phone for display (with click-to-call)
    formatPhone(phone) {
      if (!phone) return '—'
      return phone
    },

    // Toggle accordion section (multiple can be open)
    toggleSection(section) {
      this.openSections[section] = !this.openSections[section]
    },

    // Check if section is open
    isSectionOpen(section) {
      return !!this.openSections[section]
    },

    // Get Gravatar URL for person
    getGravatarUrl(size = 80) {
      return gravatarUrl(this.person?.email, size)
    },

    // ========== Enquiry-related methods ==========

    // Toggle enquiry card expansion
    toggleEnquiry(enquiryId) {
      if (this.expandedEnquiryId === enquiryId) {
        this.expandedEnquiryId = null
      } else {
        this.expandedEnquiryId = enquiryId
      }
    },

    // Check if an enquiry is expanded
    isEnquiryExpanded(enquiryId) {
      return this.expandedEnquiryId === enquiryId
    },

    // Get enquiry by ID from person data
    getEnquiry(enquiryId) {
      return this.person?.enquiries?.find(e => e.id === enquiryId)
    },

    // Add a note to the person (displayed in History section)
    async addNote() {
      if (!this.noteText.trim() || this.noteSaving) return

      this.noteSaving = true
      this.noteError = null

      try {
        const newNote = await this.saveNote({ type: 'Person', id: this.personId }, this.noteText)

        if (this.person) {
          if (!this.person.activities) {
            this.person.activities = []
          }
          this.person.activities.unshift(newNote)
        }

        this.noteText = ''
      } catch (error) {
        console.error('Failed to add note:', error)
        this.noteError = error.message || 'Failed to add note. Please try again.'
      } finally {
        this.noteSaving = false
      }
    },
    removeTimelineEntry(entryId) {
      if (this.person?.activities) {
        this.person.activities = this.person.activities.filter(a => a.id !== entryId)
      }
    },

    // Delete a note from the person
    async deleteNote(noteId) {
      if (!confirm('Delete this note?')) return
      try {
        await destroyNote(noteId)
        this.removeTimelineEntry(noteId)
      } catch (error) {
        console.error('Failed to delete note:', error)
        alert('Failed to delete note. Please try again.')
      }
    },

    // Mark enquiry as not interested
    async markNotInterested(enquiryId) {
      if (!confirm('Mark this enquiry as not interested? This will stop any automated follow-ups.')) return

      this.notInterestedSaving = true

      try {
        const response = await fetch(`/enquiries/${enquiryId}/update_status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ status: 'not_interested' })
        })

        if (!response.ok) {
          throw new Error('Failed to update status')
        }

        // Refresh person data to get updated enquiry
        await this.fetchPerson()
      } catch (error) {
        console.error('Failed to mark not interested:', error)
        alert('Failed to update enquiry status. Please try again.')
      } finally {
        this.notInterestedSaving = false
      }
    },

    // Get journey status display for an enquiry
    getJourneyStatus(enquiry) {
      if (!enquiry) return null

      if (enquiry.chase_active && enquiry.next_chase_display) {
        return {
          icon: 'ph-arrows-clockwise',
          color: 'blue',
          text: `Auto-chasing · Next: ${enquiry.next_chase_display}`
        }
      }

      if (enquiry.chase_stopped_reason) {
        // Don't show status for viewing_booked - the badge is sufficient
        if (enquiry.chase_stopped_reason === 'viewing_booked') {
          return null
        }
        const reasons = {
          'not_interested': { icon: 'ph-x-circle', color: 'gray', text: 'Auto-chasing stopped · Not interested' },
          'all_steps_exhausted': { icon: 'ph-check-circle', color: 'gray', text: 'Complete · All steps sent' },
          'property_unavailable': { icon: 'ph-house-line', color: 'orange', text: 'Auto-chasing stopped · Property unavailable' }
        }
        return reasons[enquiry.chase_stopped_reason] || { icon: 'ph-stop-circle', color: 'gray', text: 'Auto-chasing stopped' }
      }

      // Don't show "Auto-responses not enabled" for terminal enquiries
      const terminalStatuses = ['viewing_booked', 'not_interested', 'no_response', 'property_unavailable']
      if (terminalStatuses.includes(enquiry.status)) {
        return null
      }

      if (!enquiry.chase_started_at) {
        return {
          icon: 'ph-circle',
          color: 'gray',
          text: 'Auto-responses not enabled'
        }
      }

      return null
    },

    // Get timeline icon color class
    getTimelineColorClass(color) {
      const colors = {
        blue: 'bg-blue-500',
        green: 'bg-green-500',
        purple: 'bg-purple-500',
        gray: 'bg-gray-400',
        red: 'bg-red-500',
        orange: 'bg-orange-500',
        yellow: 'bg-yellow-500'
      }
      return colors[color] || 'bg-gray-400'
    },

    // Get status badge classes
    getStatusBadgeClasses(color) {
      const classes = {
        blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
        red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
        yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      }
      return classes[color] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    },

    // Format field label from snake_case to Title Case
    formatFieldLabel(key) {
      // Custom mappings for specific fields
      const labels = {
        'tenancy_length': 'Tenancy Length',
        'moving_with': 'Moving With',
        'pets': 'Pets',
        'pets_description': 'Pets Description',
        'smokers': 'Smokers',
        'first_time_renter': 'First Time Renter',
        'employment': 'Employment',
        'affordability': 'Affordability',
        'adverse_credit': 'Adverse Credit',
        'applicant_would_like': 'Applicant Would Like'
      }

      if (labels[key]) return labels[key]

      // Fallback: convert snake_case to Title Case
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    // ========== Tab Management ==========

    // Determine which tabs should be visible based on person data
    isTabVisible(tab) {
      if (!this.person) return tab === 'overview' || tab === 'history'
      switch (tab) {
        case 'overview': return true
        case 'enquiries': return (this.person.enquiry_count || 0) > 0
        case 'viewings': return this.person.viewings && this.person.viewings.length > 0
        case 'applications': return (this.person.application_count || 0) > 0
        case 'compliance': return this.person.is_tenant || this.person.is_current_tenant || this.person.is_applicant
        case 'tenancies': return (this.person.tenancy_count || 0) > 0
        case 'history': return true
        default: return false
      }
    },

    // Get default tab based on context and data
    getDefaultTab(context, focusEnquiry) {
      if (focusEnquiry) return 'enquiries'
      switch (context) {
        case 'enquiry': return 'enquiries'
        case 'viewing': return 'viewings'
        case 'tenancy': return 'tenancies'
        default: return 'overview'
      }
    },

    // Ensure active tab is valid (visible), fall back to overview
    validateActiveTab() {
      if (!this.isTabVisible(this.activeTab)) {
        this.activeTab = 'overview'
      }
    },

    // Show toast notification (adapted from viewing slideover)
    showNotification(message, type = 'success') {
      const alertClass = type === 'success'
        ? 'bg-green-100 border-green-400 text-green-700 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300'
        : 'bg-red-100 border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300'

      const flashContainer = document.getElementById('flash')
      if (!flashContainer) return

      const notification = document.createElement('div')
      notification.className = `fixed top-20 border-l-4 p-4 rounded shadow-lg w-full md:max-w-md md:right-4 md:left-auto md:transform-none left-1/2 transform -translate-x-1/2 md:-translate-x-0 ${alertClass}`
      notification.style.top = '5rem'
      notification.style.zIndex = String(this.zIndex + 50)
      notification.innerHTML = `<p class="text-sm font-medium">${message}</p>`

      flashContainer.appendChild(notification)

      setTimeout(() => {
        notification.style.transition = 'opacity 300ms ease-in'
        notification.style.opacity = '0'
        setTimeout(() => notification.remove(), 300)
      }, 4000)
    },

    // ========== Viewing Tab Methods ==========

    // Toggle/expand a viewing in the Viewings tab
    async toggleViewing(viewingId) {
      if (this.expandedViewingId === viewingId) {
        this.expandedViewingId = null
        this.expandedViewing = null
        this.resetViewingActionState()
        return
      }

      this.expandedViewingId = viewingId
      this.expandedViewing = null
      this.resetViewingActionState()
      await this.fetchViewingDetails(viewingId)
    },

    isViewingExpanded(viewingId) {
      return this.expandedViewingId === viewingId
    },

    // Fetch full viewing data for expanded view
    async fetchViewingDetails(viewingId) {
      this.viewingLoading = true
      try {
        const response = await fetch(`/viewings/${viewingId}.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
        this.expandedViewing = await response.json()
        this.vwNotes = this.expandedViewing.notes || ''
      } catch (error) {
        console.error('Failed to fetch viewing details:', error)
        this.expandedViewing = null
      } finally {
        this.viewingLoading = false
      }
    },

    // Reset viewing action state
    resetViewingActionState() {
      this.vwShowCancelForm = false
      this.vwShowRescheduleForm = false
      this.vwShowRejectForm = false
      this.vwCancelReason = ''
      this.vwRejectReason = ''
      this.vwCancelling = false
      this.vwRejecting = false
      this.vwRescheduling = false
      this.vwConfirming = false
      this.vwCompleting = false
      this.vwMarkingNoShow = false
      this.vwSendingPostViewingEmail = false
      this.vwSavingNotes = false
      this.vwNotes = ''
      this.vwRescheduleSlots = []
      this.vwLoadingRescheduleSlots = false
      this.vwRescheduleDate = ''
      this.vwRescheduleTime = ''
      this.vwRescheduleUseCustomTime = false
      this.vwRescheduleReason = ''
    },

    // Update the viewing in person.viewings array after an action
    updateViewingInList(updatedViewing) {
      if (!this.person?.viewings) return
      const idx = this.person.viewings.findIndex(v => v.id === updatedViewing.id)
      if (idx !== -1) {
        // Update summary fields from full viewing data
        this.person.viewings[idx] = {
          ...this.person.viewings[idx],
          status: updatedViewing.status,
          status_color: updatedViewing.status_color,
          status_display: updatedViewing.status_display,
          scheduled_at: updatedViewing.scheduled_at,
          scheduled_date_display: updatedViewing.scheduled_date_display,
          scheduled_time_display: updatedViewing.scheduled_time_display
        }
      }
    },

    // Viewing status badge helpers
    getViewingStatusBadgeColor(status) {
      switch (status) {
        case 'confirmed': return 'green'
        case 'pending_approval': return 'amber'
        case 'completed': return 'blue'
        case 'cancelled': return 'red'
        case 'no_show': return 'red'
        case 'rejected': return 'red'
        default: return 'gray'
      }
    },

    getViewingStatusText(status) {
      const statusMap = {
        'pending_approval': 'Pending Approval',
        'confirmed': 'Confirmed',
        'completed': 'Completed',
        'cancelled': 'Cancelled',
        'no_show': 'No Show',
        'rejected': 'Rejected'
      }
      return statusMap[status] || status
    },

    // Check if viewing is in the past
    isViewingPast(scheduledAt) {
      if (!scheduledAt) return false
      return new Date(scheduledAt) < new Date()
    },

    // Format date/time helpers for viewings
    formatViewingDateTime(dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    },

    formatViewingDate(dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
      })
    },

    formatViewingTime(dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    },

    // Confirm a pending viewing
    async vwConfirmViewing() {
      if (!this.expandedViewing) return
      this.vwConfirming = true
      try {
        const response = await fetch(`/viewings/${this.expandedViewing.id}/confirm.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (response.ok) {
          const data = await response.json()
          this.expandedViewing = data.viewing || data
          this.updateViewingInList(this.expandedViewing)
          window.dispatchEvent(new CustomEvent('viewing-updated', { detail: { viewing: this.expandedViewing } }))
          this.showNotification('Viewing confirmed successfully')
        } else {
          this.showNotification('Failed to confirm viewing', 'error')
        }
      } catch (error) {
        console.error('Failed to confirm viewing:', error)
        this.showNotification('Failed to confirm viewing', 'error')
      } finally {
        this.vwConfirming = false
      }
    },

    // Reject a pending viewing
    async vwRejectViewing() {
      if (!this.expandedViewing) return
      this.vwRejecting = true
      try {
        const response = await fetch(`/viewings/${this.expandedViewing.id}/reject.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ reason: this.vwRejectReason })
        })
        if (response.ok) {
          const data = await response.json()
          this.expandedViewing = data.viewing || data
          this.updateViewingInList(this.expandedViewing)
          this.vwShowRejectForm = false
          this.vwRejectReason = ''
          window.dispatchEvent(new CustomEvent('viewing-updated', { detail: { viewing: this.expandedViewing } }))
          this.showNotification('Viewing rejected')
        } else {
          this.showNotification('Failed to reject viewing', 'error')
        }
      } catch (error) {
        console.error('Failed to reject viewing:', error)
        this.showNotification('Failed to reject viewing', 'error')
      } finally {
        this.vwRejecting = false
      }
    },

    // Cancel a viewing
    async vwCancelViewing() {
      if (!this.expandedViewing) return
      this.vwCancelling = true
      try {
        const response = await fetch(`/viewings/${this.expandedViewing.id}/cancel.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ reason: this.vwCancelReason })
        })
        if (response.ok) {
          const data = await response.json()
          this.expandedViewing = data.viewing || data
          this.updateViewingInList(this.expandedViewing)
          this.vwShowCancelForm = false
          this.vwCancelReason = ''
          window.dispatchEvent(new CustomEvent('viewing-updated', { detail: { viewing: this.expandedViewing } }))
          this.showNotification('Viewing cancelled')
        } else {
          const data = await response.json().catch(() => ({}))
          this.showNotification(data.error || 'Failed to cancel viewing', 'error')
        }
      } catch (error) {
        console.error('Failed to cancel viewing:', error)
        this.showNotification('Failed to cancel viewing', 'error')
      } finally {
        this.vwCancelling = false
      }
    },

    // Mark viewing as completed
    async vwCompleteViewing() {
      if (!this.expandedViewing) return
      this.vwCompleting = true
      try {
        const response = await fetch(`/viewings/${this.expandedViewing.id}/complete.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (response.ok) {
          const data = await response.json()
          this.expandedViewing = data.viewing || data
          this.updateViewingInList(this.expandedViewing)
          window.dispatchEvent(new CustomEvent('viewing-updated', { detail: { viewing: this.expandedViewing } }))
          this.showNotification('Viewing marked as completed')
        } else {
          this.showNotification('Failed to complete viewing', 'error')
        }
      } catch (error) {
        console.error('Failed to complete viewing:', error)
        this.showNotification('Failed to complete viewing', 'error')
      } finally {
        this.vwCompleting = false
      }
    },

    // Mark viewing as no-show
    async vwMarkNoShow() {
      if (!this.expandedViewing) return
      this.vwMarkingNoShow = true
      try {
        const response = await fetch(`/viewings/${this.expandedViewing.id}/no_show.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (response.ok) {
          const data = await response.json()
          this.expandedViewing = data.viewing || data
          this.updateViewingInList(this.expandedViewing)
          window.dispatchEvent(new CustomEvent('viewing-updated', { detail: { viewing: this.expandedViewing } }))
          this.showNotification('Viewing marked as no-show')
        } else {
          this.showNotification('Failed to mark no-show', 'error')
        }
      } catch (error) {
        console.error('Failed to mark no-show:', error)
        this.showNotification('Failed to mark no-show', 'error')
      } finally {
        this.vwMarkingNoShow = false
      }
    },

    // Send post-viewing email
    async vwSendPostViewingEmail() {
      if (!this.expandedViewing) return
      this.vwSendingPostViewingEmail = true
      try {
        const response = await fetch(`/viewings/${this.expandedViewing.id}/send_post_viewing_email.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (response.ok) {
          const data = await response.json()
          this.expandedViewing = data.viewing || data
          this.updateViewingInList(this.expandedViewing)
          window.dispatchEvent(new CustomEvent('viewing-updated', { detail: { viewing: this.expandedViewing } }))
          this.showNotification('Post-viewing email queued')
        } else {
          const errorData = await response.json()
          this.showNotification(errorData.error || 'Failed to send email', 'error')
        }
      } catch (error) {
        console.error('Failed to send post-viewing email:', error)
        this.showNotification('Failed to send email', 'error')
      } finally {
        this.vwSendingPostViewingEmail = false
      }
    },

    // Save viewing notes (auto-save)
    async vwSaveNotes() {
      if (!this.expandedViewing || this.vwSavingNotes) return
      if (this.vwNotes === (this.expandedViewing.notes || '')) return
      this.vwSavingNotes = true
      try {
        const response = await fetch(`/viewings/${this.expandedViewing.id}.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ viewing: { notes: this.vwNotes } })
        })
        if (response.ok) {
          const data = await response.json()
          this.expandedViewing = data
        }
      } catch (error) {
        console.error('Failed to save notes:', error)
      } finally {
        this.vwSavingNotes = false
      }
    },

    // Open reschedule form and fetch slots
    async vwOpenRescheduleForm() {
      this.vwRescheduleDate = ''
      this.vwRescheduleTime = ''
      this.vwRescheduleUseCustomTime = false
      this.vwRescheduleSlots = []
      this.vwRescheduleReason = ''
      this.vwLoadingRescheduleSlots = true
      await this.vwFetchRescheduleSlots()
      this.vwShowRescheduleForm = true
    },

    vwCloseRescheduleForm() {
      this.vwShowRescheduleForm = false
      this.vwRescheduleDate = ''
      this.vwRescheduleTime = ''
      this.vwRescheduleUseCustomTime = false
      this.vwRescheduleSlots = []
      this.vwRescheduleReason = ''
    },

    async vwFetchRescheduleSlots() {
      if (!this.expandedViewing?.property_id) {
        this.vwRescheduleSlots = []
        return
      }
      this.vwLoadingRescheduleSlots = true
      try {
        const from = new Date()
        const to = new Date()
        to.setDate(to.getDate() + 14)
        const fromStr = from.toISOString().split('T')[0]
        const toStr = to.toISOString().split('T')[0]
        const response = await fetch(`/api/viewing_slots?property_id=${this.expandedViewing.property_id}&from=${fromStr}&to=${toStr}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (response.ok) {
          const data = await response.json()
          this.vwRescheduleSlots = data.slots || []
        } else {
          this.vwRescheduleSlots = []
        }
      } catch (error) {
        console.error('Failed to fetch reschedule slots:', error)
        this.vwRescheduleSlots = []
      } finally {
        this.vwLoadingRescheduleSlots = false
      }
    },

    get vwRescheduleAvailableDates() {
      const dates = new Map()
      for (const slot of this.vwRescheduleSlots) {
        const dateStr = slot.date
        if (!dates.has(dateStr)) {
          const date = new Date(dateStr + 'T00:00:00')
          dates.set(dateStr, {
            value: dateStr,
            day: date.toLocaleDateString('en-GB', { weekday: 'short' }),
            date: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          })
        }
      }
      return Array.from(dates.values()).slice(0, 7)
    },

    get vwRescheduleSlotsForSelectedDate() {
      if (!this.vwRescheduleDate) return []
      return this.vwRescheduleSlots
        .filter(slot => slot.date === this.vwRescheduleDate)
        .sort((a, b) => a.time.localeCompare(b.time))
    },

    vwSelectRescheduleDate(dateValue) {
      this.vwRescheduleDate = dateValue
      this.vwRescheduleTime = ''
    },

    vwSelectRescheduleTimeSlot(time) {
      this.vwRescheduleTime = time
    },

    vwHandleRescheduleCustomDatetimeChange(event) {
      const value = event.target.value
      if (value) {
        const [datePart, timePart] = value.split(' ')
        this.vwRescheduleDate = datePart
        this.vwRescheduleTime = timePart
      }
    },

    async vwRescheduleViewing() {
      if (!this.expandedViewing || !this.vwRescheduleDate || !this.vwRescheduleTime) return
      this.vwRescheduling = true
      const scheduledAt = `${this.vwRescheduleDate}T${this.vwRescheduleTime}:00`
      try {
        const response = await fetch(`/viewings/${this.expandedViewing.id}.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            viewing: { scheduled_at: scheduledAt, reschedule_reason: this.vwRescheduleReason || null }
          })
        })
        if (response.ok) {
          const data = await response.json()
          this.expandedViewing = data
          this.updateViewingInList(this.expandedViewing)
          this.vwCloseRescheduleForm()
          window.dispatchEvent(new CustomEvent('viewing-updated', { detail: { viewing: this.expandedViewing } }))
          this.showNotification('Viewing rescheduled successfully')
        } else {
          const errorData = await response.json()
          const errorMessage = errorData.errors?.scheduled_at?.[0] ||
                               errorData.errors?.base?.[0] ||
                               Object.values(errorData.errors || {}).flat()[0] ||
                               'Failed to reschedule viewing'
          this.showNotification(`Reschedule failed: ${errorMessage}`, 'error')
        }
      } catch (error) {
        console.error('Failed to reschedule viewing:', error)
        this.showNotification('Failed to reschedule viewing', 'error')
      } finally {
        this.vwRescheduling = false
      }
    },

    // All compliance/document-upload methods are provided by complianceMixin()
  })
}
